import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import versions from '../content/legalVersions.json';
import { LEGAL_DOCS } from '../content/legalDocs';
import { OPTIONAL_CATEGORIES, readChoices, saveChoices } from '../lib/privacyChoices';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

export const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function Block({ b }) {
  if (typeof b === 'string') return <p>{b}</p>;
  if (b.list) return <ul className="list-disc pl-5 space-y-1.5">{b.list.map((x, i) => <li key={i}>{x}</li>)}</ul>;
  if (b.review) {
    return (
      <p className="text-[13px] rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-amber-200/90 dark:text-amber-200/90" data-testid="legal-review-note">
        <strong className="font-semibold">For owner/legal review:</strong> {b.review}
      </p>
    );
  }
  return null;
}

/** Cookie preferences: honest about there being nothing optional today. */
function Preferences() {
  const [choices, setChoices] = useState(() => readChoices()?.granted || {});
  const [saved, setSaved] = useState(false);
  const save = (granted) => { saveChoices(granted); setChoices(granted); setSaved(true); };
  return (
    <section id="preferences" className="mg-card-elevated p-5 mt-8 scroll-mt-24" data-testid="cookie-preferences">
      <h2 className="font-heading text-lg font-bold">Manage preferences</h2>
      {OPTIONAL_CATEGORIES.length === 0 ? (
        <p className="mt-2 text-muted-foreground">
          Midgard currently uses no optional cookies or trackers, so there is nothing to switch on or off. If that
          changes, each optional category will appear here switched off, and you will be asked before anything optional runs.
        </p>
      ) : (
        <>
          <div className="mt-3 space-y-3">
            {OPTIONAL_CATEGORIES.map((c) => (
              <label key={c.id} className="flex items-start gap-3">
                <input type="checkbox" className="mt-1" checked={!!choices[c.id]}
                  onChange={(e) => setChoices({ ...choices, [c.id]: e.target.checked })} />
                <span><span className="font-semibold">{c.label}</span><br /><span className="text-sm text-muted-foreground">{c.description}</span></span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button className="mg-btn mg-btn-secondary px-4 h-10" onClick={() => save({})}>Reject optional</button>
            <button className="mg-btn mg-btn-secondary px-4 h-10" onClick={() => save(choices)}>Save choices</button>
            <button className="mg-btn mg-btn-secondary px-4 h-10"
              onClick={() => save(Object.fromEntries(OPTIONAL_CATEGORIES.map((c) => [c.id, true])))}>Accept all</button>
          </div>
          {saved && <p className="mt-2 text-sm text-muted-foreground">Saved. You can change this at any time.</p>}
        </>
      )}
    </section>
  );
}

function MyAcceptance({ docId }) {
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get('/legal/me').then((r) => setRec(r.data?.accepted?.[docId] || null)).catch(() => {});
  }, [user, docId]);
  if (!rec) return null;
  return (
    <p className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground">
      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      You accepted version {rec.version} on {new Date(rec.accepted_at).toLocaleDateString()}.
    </p>
  );
}

export default function LegalPage() {
  const { docId } = useParams();
  const { hash } = useLocation();
  const meta = versions.documents[docId];
  const doc = LEGAL_DOCS[docId];

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) el.scrollIntoView({ block: 'start' });
  }, [hash, docId]);

  if (!meta || !doc) return <Navigate to="/about#legal" replace />;
  const draft = versions.status === 'draft';

  return (
    <article className="mg-page max-w-3xl py-6 sm:py-10" data-testid={`legal-page-${docId}`}>
      <p className="text-[12px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2">
        <FileText className="w-4 h-4" /> Midgard legal
      </p>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-2">{meta.title}</h1>
      <p className="mt-2 text-muted-foreground">{doc.summary}</p>
      <p className="mt-2 text-[13px] text-muted-foreground">Version {meta.version}</p>
      <MyAcceptance docId={docId} />

      {draft && (
        <div className="mt-5 flex gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-[14px]" role="note" data-testid="legal-draft-banner">
          <AlertTriangle className="w-5 h-5 shrink-0 text-amber-500 mt-0.5" />
          <p>
            <strong>Draft under review.</strong> This document is being finalised and is not yet in force. It describes how
            Midgard works today and is published so members can read it ahead of time. Items marked for review will be completed before it takes effect.
          </p>
        </div>
      )}

      <div className="mt-8 space-y-8 leading-relaxed text-[15px]">
        {doc.sections.map((s) => (
          <section key={s.h} id={slug(s.h)} className="scroll-mt-24 space-y-3">
            <h2 className="font-heading text-lg sm:text-xl font-bold">{s.h}</h2>
            {s.body.map((b, i) => <Block key={i} b={b} />)}
          </section>
        ))}
      </div>

      {docId === 'cookies' && <Preferences />}

      <nav className="mt-12 pt-6 border-t border-[hsl(var(--mg-steel)/0.15)]" aria-label="Other documents">
        <p className="text-[12px] uppercase tracking-[0.16em] text-muted-foreground mb-3">Other documents</p>
        <ul className="flex flex-wrap gap-x-5 gap-y-2 text-[14px]">
          {Object.entries(versions.documents).filter(([id]) => id !== docId).map(([id, d]) => (
            <li key={id}><Link to={`/legal/${id}`} className="text-[hsl(var(--mg-purple))] hover:underline">{d.title}</Link></li>
          ))}
          <li><Link to="/about" className="text-[hsl(var(--mg-purple))] hover:underline">About Midgard</Link></li>
        </ul>
      </nav>
    </article>
  );
}
